/**
 * HeartPolicyPage - 하트 정책 안내 페이지
 *
 * Silver Connect 하트 정책 v3를 시니어 친화적으로 안내합니다.
 *
 * @author 프론트맨 (Frontend Developer)
 * @version v3
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
} from '@mui/material';
import { ArrowBack, Favorite } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { HEART_COSTS, CARD_EXTENSION_DAYS, REWARDS } from '../../types/heart';

const HeartPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FA' }}>
      {/* 헤더 */}
      <AppBar position="sticky" elevation={2} sx={{ bgcolor: 'white' }}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate(-1)}
            sx={{ mr: 2, color: '#0D1F3C' }}
            aria-label="뒤로 가기"
          >
            <ArrowBack sx={{ fontSize: 28 }} />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Favorite sx={{ fontSize: 32, color: '#B87D8F', mr: 1.5 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#0D1F3C',
                fontSize: 24,
              }}
            >
              하트 정책 안내
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 본문 */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* 하트란? */}
        <Paper elevation={2} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ fontSize: 48, mr: 2 }}>❤️</Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 28 }}>
              하트란?
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 20, lineHeight: 1.8, color: '#4A5A72' }}>
            하트는 실버커넥트에서 인연을 만나기 위해 사용하는 <strong>포인트</strong>입니다.
            <br />
            카드를 열거나 연장할 때 사용하며, <strong>유효기간 없이</strong> 원하실 때 사용하실 수 있어요.
          </Typography>
        </Paper>

        {/* 하트 사용처 */}
        <Paper elevation={2} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ fontSize: 48, mr: 2 }}>💳</Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 28 }}>
              하트 사용처
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 카드 오픈 */}
            <Box>
              <Box
                sx={{
                  bgcolor: '#F0F3F8',
                  borderRadius: 2,
                  p: 3,
                  border: '2px solid #E2E8F0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ fontSize: 36, mr: 2 }}>💘</Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#B87D8F' }}>
                    큐피트 카드 오픈
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 20, color: '#4A5A72' }}>사용 하트</Typography>
                  <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#B87D8F' }}>
                    {HEART_COSTS.CARD_OPEN}하트
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 18, color: '#8896AB', lineHeight: 1.6 }}>
                  • 카드를 열면 상대방의 상세 정보를 볼 수 있어요
                  <br />
                  • 상대방과 <strong>무제한 채팅</strong>이 가능해요
                  <br />• 카드는 발급 시점부터 <strong>3일간</strong> 유효해요
                </Typography>
              </Box>
            </Box>

            {/* 채팅 */}
            <Box>
              <Box
                sx={{
                  bgcolor: '#F0F3F8',
                  borderRadius: 2,
                  p: 3,
                  border: '2px solid #E2E8F0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ fontSize: 36, mr: 2 }}>💬</Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#C9A86C' }}>
                    채팅 메시지
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 20, color: '#4A5A72' }}>사용 하트</Typography>
                  <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#C9A86C' }}>
                    무료!
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 18, color: '#8896AB', lineHeight: 1.6 }}>
                  • 오픈한 카드와는 <strong>무제한 대화</strong>가 가능해요
                  <br />
                  • 메시지 횟수 제한이 없어요
                  <br />• 카드 유효기간 동안 자유롭게 대화하세요
                </Typography>
              </Box>
            </Box>

            {/* 카드 연장 */}
            <Box>
              <Box
                sx={{
                  bgcolor: '#FFF5F0',
                  borderRadius: 2,
                  p: 3,
                  border: '2px solid #FFD5C2',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ fontSize: 36, mr: 2 }}>⏰</Box>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#C9A86C' }}>
                    카드 연장
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 20, color: '#4A5A72' }}>사용 하트</Typography>
                  <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#C9A86C' }}>
                    {HEART_COSTS.CARD_EXTENSION}하트
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 18, color: '#8896AB', lineHeight: 1.6 }}>
                  • 유효기간을 <strong>{CARD_EXTENSION_DAYS}일 연장</strong>할 수 있어요
                  <br />
                  • 카드당 <strong>1회만</strong> 연장 가능해요
                  <br />
                  • 유효기간 내에서만 연장할 수 있어요
                  <br />• 최대 유효기간은 <strong>6일</strong>이에요 (기본 3일 + 연장 3일)
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* 하트 받는 방법 */}
        <Paper elevation={2} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ fontSize: 48, mr: 2 }}>🎁</Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 28 }}>
              하트 받는 방법
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* 신규 가입 */}
            <Box
              sx={{
                bgcolor: '#F5F7FA',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ fontSize: 32, mr: 2 }}>👋</Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0D1F3C' }}>
                    신규 가입
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#B87D8F' }}>
                  +{REWARDS.SIGNUP}하트
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 18, color: '#8896AB' }}>
                실버커넥트에 가입하시면 환영 선물로 드려요
              </Typography>
            </Box>

            {/* 프로필 완성 */}
            <Box
              sx={{
                bgcolor: '#F5F7FA',
                borderRadius: 2,
                p: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ fontSize: 32, mr: 2 }}>✍️</Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0D1F3C' }}>
                    프로필 완성
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#B87D8F' }}>
                  +{REWARDS.PROFILE_COMPLETE}하트
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 18, color: '#8896AB' }}>
                자기소개, 관심사, 사진 5장을 모두 등록하면 드려요
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            sx={{
              fontSize: 18,
              color: '#8896AB',
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            💡 처음 가입하시면 최대 <strong style={{ color: '#B87D8F' }}>10하트</strong>를 받으실 수
            있어요!
            <br />
            (가입 {REWARDS.SIGNUP}하트 + 프로필 완성 {REWARDS.PROFILE_COMPLETE}하트)
          </Typography>
        </Paper>

        {/* 하트 충전 */}
        <Paper elevation={2} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ fontSize: 48, mr: 2 }}>💰</Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 28 }}>
              하트 충전
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 20, color: '#4A5A72', mb: 3, lineHeight: 1.6 }}>
            하트가 부족하시면 언제든지 충전하실 수 있어요.
            <br />
            다양한 충전 옵션을 준비했습니다.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 충전 패키지 예시 */}
            <Box
              sx={{
                bgcolor: '#F5F7FA',
                borderRadius: 2,
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ fontSize: 20, fontWeight: 600 }}>10 하트</Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#B87D8F' }}>
                ₩5,000
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: '#F5F7FA',
                borderRadius: 2,
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 600 }}>30 하트</Typography>
                <Typography sx={{ fontSize: 16, color: '#C9A86C' }}>7% 할인</Typography>
              </Box>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#B87D8F' }}>
                ₩14,000
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: '#F0F3F8',
                borderRadius: 2,
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '2px solid #E2E8F0',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>100 하트</Typography>
                <Typography sx={{ fontSize: 16, color: '#C9A86C', fontWeight: 600 }}>
                  22% 할인 (가장 인기)
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#B87D8F' }}>
                ₩39,000
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 주요 안내 */}
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: '#F5F7FA',
            border: '2px solid #E2E8F0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ fontSize: 48, mr: 2 }}>📌</Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 28, color: '#0D1F3C' }}>
              꼭 기억하세요
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: 20, color: '#4A5A72', lineHeight: 1.8 }}>
              ✓ 하트는 <strong>유효기간이 없어요</strong> - 원하실 때 사용하세요
            </Typography>
            <Typography sx={{ fontSize: 20, color: '#4A5A72', lineHeight: 1.8 }}>
              ✓ 카드 유효기간은 발급 시점부터 <strong>3일</strong>이에요
            </Typography>
            <Typography sx={{ fontSize: 20, color: '#4A5A72', lineHeight: 1.8 }}>
              ✓ 카드 연장은 <strong>1회만</strong> 가능해요 (+3일)
            </Typography>
            <Typography sx={{ fontSize: 20, color: '#4A5A72', lineHeight: 1.8 }}>
              ✓ 채팅은 <strong>무제한 무료</strong>예요
            </Typography>
            <Typography sx={{ fontSize: 20, color: '#4A5A72', lineHeight: 1.8 }}>
              ✓ 한 번 오픈한 카드는 <strong>되돌릴 수 없어요</strong>
            </Typography>
          </Box>
        </Paper>

        {/* 하단 여백 */}
        <Box sx={{ height: 40 }} />
      </Container>
    </Box>
  );
};

export default HeartPolicyPage;
